import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Copy, Check, Lock, ChevronDown, Info } from "lucide-react";
import TokenIcon from "./TokenIcon";
import { formatCompactBalance, addThousandsSeparators, formatPlainDecimal } from "@/lib/utils";
import { balanceToUsd, formatUsdAmount, formatUsdTotal } from "@/lib/tokenUsdValue";
import { useTokenPrices } from "@/hooks/useTokenPrices";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";

interface AddressTooltipContentProps {
  address?: string;
  explorerBaseUrl: string;
  copiedAddress: string | null;
  onCopy: (address: string) => void;
}

const AddressTooltipContent = ({
  address,
  explorerBaseUrl,
  copiedAddress,
  onCopy,
}: AddressTooltipContentProps) => {
  if (!address) {
    return (
      <p className="font-mono text-xs text-foreground chip-bg px-2 py-1 rounded">
        Address not available
      </p>
    );
  }
  return (
    <div className="flex items-center gap-2 chip-bg px-2 py-1 rounded whitespace-nowrap">
      <a
        href={`${explorerBaseUrl}/address/${address}?tab=contract`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="font-mono text-xs text-foreground hover:underline hover:text-blue-400 transition-colors whitespace-nowrap"
      >
        {address}
      </a>
      <button
        type="button"
        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onCopy(address); }}
        className="shrink-0 text-muted-foreground hover:text-foreground transition-colors p-1"
        aria-label="Copy address"
      >
        {copiedAddress === address ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      </button>
    </div>
  );
};

export interface TokenPair {
  symbol: string;
  name: string;
  icon: string;
  publicBalance: string;
  privateBalance: string;
  privateSymbol: string;
  publicAddress?: string;
  privateAddress?: string;
}

interface CombinedTokenCardsProps {
  pairs: TokenPair[];
  selectedToken: string | null;
  selectedSide: "public" | "private" | null;
  onSelectPublicToken: (symbol: string) => void;
  onSelectPrivateToken: (symbol: string) => void;
  isPrivateUnlocked?: boolean;
  /** Opens the unlock modal without starting silent unlock or AES retrieval. */
  onShowUnlockModal?: () => void;
  explorerBaseUrl?: string;
}

/**
 * --pub-min / --prv-min are set on the component root to the widest formatted
 * public/private balance, so each amount column reserves exactly the space it
 * needs, never truncates, and stays aligned across rows.
 * Every row (and the header) is its own grid, so cross-row alignment requires
 * track *bases* that don't depend on each row's content: Asset is a fixed
 * 11rem and the other columns are var-driven (--pub-min/--prv-min/--tot-min;
 * max-content bases starve the other tracks per-row and cause drift).
 * Stretching is row-safe once bases are uniform, so Private and Total both
 * take 1fr: surplus width splits evenly between the Public↔Private gap and
 * the Private↔Total gap (amounts are right-aligned, so each track's share
 * shows up on its left). Asset↔Public stays packed.
 * (Class strings must stay literal for the Tailwind scanner.)
 */
const TOKEN_ROW_GRID =
  "grid grid-cols-2 sm:grid-cols-[11rem_minmax(10rem,calc(var(--pub-min)_+_6.5rem))_minmax(max(10rem,calc(var(--prv-min)_+_6rem)),1fr)_minmax(calc(var(--tot-min)_+_1.5rem),1fr)] gap-2";

const TOKEN_HEADER_GRID =
  "grid grid-cols-[11rem_minmax(10rem,calc(var(--pub-min)_+_6.5rem))_minmax(max(10rem,calc(var(--prv-min)_+_6rem)),1fr)_minmax(calc(var(--tot-min)_+_1.5rem),1fr)] gap-2";

/** Two-column layout inside a balance cell: right-aligned amount + fixed-width symbol column. */
const PUBLIC_CELL_GRID = "grid grid-cols-[minmax(var(--pub-min),1fr)_4rem] gap-1 sm:gap-2 min-w-0";
const PRIVATE_CELL_GRID = "grid grid-cols-[minmax(var(--prv-min),1fr)_3.5rem] gap-1 sm:gap-2 min-w-0";

/** Mobile card: shared amount + symbol columns so Public/Private rows align. */
const MOBILE_BALANCE_LINE =
  "grid w-full grid-cols-[minmax(0,1fr)_4.5rem] items-baseline gap-x-2";

const MOBILE_SLIDE_OFFSET = 40;

type MobileSlideCustom = { direction: 1 | -1; version: number };

const mobileCardVariants = {
  enter: (custom: MobileSlideCustom) => ({
    x: custom.direction > 0 ? MOBILE_SLIDE_OFFSET : -MOBILE_SLIDE_OFFSET,
    y: 0,
    opacity: 0,
  }),
  center: { x: 0, y: 0, opacity: 1 },
  exit: (custom: MobileSlideCustom) => ({
    x: custom.direction > 0 ? -MOBILE_SLIDE_OFFSET : MOBILE_SLIDE_OFFSET,
    y: 0,
    opacity: 0,
  }),
};

/** Compact K/M/B for large amounts; otherwise always 2 decimals (e.g. 5 → 5.00). */
const formatSmartBalance = (balance: string) => {
  const cleaned = formatPlainDecimal(balance.replace(/,/g, ""));
  const num = parseFloat(cleaned);
  if (!Number.isFinite(num)) return balance;
  if (Math.abs(num) >= 1_000) {
    return formatCompactBalance(cleaned);
  }
  const sign = num < 0 ? "-" : "";
  return `${sign}${addThousandsSeparators(Math.abs(num).toFixed(2))}`;
};

const CombinedTokenCards = ({
  pairs,
  selectedToken,
  selectedSide,
  onSelectPublicToken,
  onSelectPrivateToken,
  isPrivateUnlocked = true,
  onShowUnlockModal,
  explorerBaseUrl = "https://testnet.cotiscan.io",
}: CombinedTokenCardsProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);
  const [mobileIndex, setMobileIndex] = useState(0);
  const [mobileSlideCustom, setMobileSlideCustom] = useState<MobileSlideCustom>({ direction: 1, version: 0 });
  const [mobileWrapHeight, setMobileWrapHeight] = useState<number | null>(null);
  const touchStartX = useRef<number | null>(null);
  const mobileSlideRef = useRef<HTMLDivElement>(null);
  const { prices } = useTokenPrices();

  const handleCopyAddress = (address: string) => {
    navigator.clipboard.writeText(address);
    setCopiedAddress(address);
    setTimeout(() => setCopiedAddress(null), 2000);
  };

  const filteredPairs = pairs.filter(
    (p) =>
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.symbol.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.privateSymbol.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Widest formatted balances (in characters) drive the amount column widths.
  // The 1.2 factor buffers the ch unit against the bold tabular-nums amount font.
  const maxPublicChars = Math.max(
    8,
    ...pairs.map(p => formatSmartBalance(p.publicBalance).length),
  );
  const maxPrivateChars = Math.max(
    6,
    ...pairs.map(p => (isPrivateUnlocked ? formatSmartBalance(p.privateBalance).length : 4)),
  );
  // The 13-char floor keeps the nowrap "Total Value ⓘ" header label fitting.
  const maxTotalChars = Math.max(
    13,
    ...pairs.map(p => {
      if (!isPrivateUnlocked) return 6;
      const totalUsd =
        balanceToUsd(p.symbol, p.publicBalance, prices) +
        balanceToUsd(p.symbol, p.privateBalance, prices);
      return `$ ${formatUsdTotal(totalUsd)}`.length;
    }),
  );
  const balanceColStyle = {
    "--pub-min": `${(maxPublicChars * 1.2).toFixed(1)}ch`,
    "--prv-min": `${(maxPrivateChars * 1.2).toFixed(1)}ch`,
    // Totals use a tighter 1.05 buffer: commas/space are narrower than 1ch,
    // so per-char reserve already overshoots — a wide buffer just reopens the
    // Private↔Total gap.
    "--tot-min": `${(maxTotalChars * 1.05).toFixed(1)}ch`,
  } as React.CSSProperties;

  const safeMobileIndex = Math.min(mobileIndex, Math.max(0, pairs.length - 1));

  const syncMobileWrapHeight = useCallback(() => {
    if (mobileSlideRef.current) {
      setMobileWrapHeight(mobileSlideRef.current.offsetHeight);
    }
  }, []);

  useEffect(() => {
    syncMobileWrapHeight();
  }, [safeMobileIndex, pairs, isPrivateUnlocked, prices, syncMobileWrapHeight]);

  const navigateMobile = (targetIndex: number) => {
    const clamped = Math.max(0, Math.min(targetIndex, pairs.length - 1));
    if (clamped === safeMobileIndex) return;
    syncMobileWrapHeight();
    const direction: 1 | -1 = clamped > safeMobileIndex ? 1 : -1;
    setMobileSlideCustom((prev) => ({ direction, version: prev.version + 1 }));
    setMobileIndex(clamped);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(delta) > 40) {
      navigateMobile(delta > 0 ? safeMobileIndex + 1 : safeMobileIndex - 1);
    }
    touchStartX.current = null;
  };

  return (
    <div className="w-full" style={balanceColStyle}>
      {/* ── MOBILE: token dropdown selector ── */}
      <div className="sm:hidden mb-2">
        <div className="relative">
          <select
            value={safeMobileIndex}
            onChange={(e) => navigateMobile(Number(e.target.value))}
            className="w-full appearance-none bg-muted/50 border border-border rounded-xl px-3 py-2 pr-8 text-foreground text-sm font-medium outline-none focus:border-border transition-colors"
            aria-label="Select token"
          >
            {pairs.map((p, i) => (
              <option key={p.symbol} value={i}>
                {p.name} ({p.symbol})
              </option>
            ))}
          </select>
          <ChevronDown
            className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
            aria-hidden="true"
          />
        </div>
      </div>

      {/* ── DESKTOP: search bar (sm and above) ── */}
      <div className="hidden sm:block mb-4">
        <label className="flex items-center gap-2 px-4 py-2 rounded-xl bg-muted/50 border border-border focus-within:border-border transition-colors">
          <Search className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden="true" />
          <input
            type="text"
            placeholder="Search tokens..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 bg-transparent text-foreground text-sm outline-none placeholder:text-muted-foreground"
            aria-label="Search for tokens"
          />
        </label>
      </div>

      {/* ── MOBILE: one token at a time, navigated via dropdown ── */}
      <div className="sm:hidden">
        {pairs.length === 0 ? (
          <div className="text-center text-muted-foreground text-sm py-8">No tokens found</div>
        ) : (
          <div
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            className="relative overflow-x-hidden"
            style={mobileWrapHeight ? { height: mobileWrapHeight } : undefined}
          >
            <AnimatePresence initial={false} custom={mobileSlideCustom}>
              {pairs[safeMobileIndex] && (() => {
                  const pair = pairs[safeMobileIndex];
                  const isPublicSelected = selectedSide === "public" && selectedToken === pair.symbol;
                  const isPrivateSelected = selectedSide === "private" && selectedToken === pair.privateSymbol;
                  const publicUsd = balanceToUsd(pair.symbol, pair.publicBalance, prices);
                  const privateUsd = balanceToUsd(pair.symbol, pair.privateBalance, prices);
                  const combinedUsd = publicUsd + privateUsd;
                  return (
                    <motion.div
                      ref={mobileSlideRef}
                      key={pair.symbol}
                      custom={mobileSlideCustom}
                      variants={mobileCardVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{ duration: 0.18 }}
                      onAnimationComplete={syncMobileWrapHeight}
                      className={`w-full rounded-xl bg-muted/50 border border-border px-3 py-3 flex flex-col gap-2 ${
                        mobileSlideCustom.version === 0 ? "relative" : "absolute inset-x-0 top-0"
                      }`}
                    >
                      {/* Token header row */}
                      <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 gap-y-0.5">
                        <div className="row-span-2 self-center">
                          <TokenIcon icon={pair.icon} size="md" />
                        </div>
                        <span className="text-foreground/90 dark:text-cyan-300/70 text-sm font-medium leading-tight">
                          {pair.name}
                        </span>
                        <div className="flex min-w-0 items-baseline justify-between gap-2">
                          <span className="text-muted-foreground text-xs">{pair.symbol}</span>
                          <span className="shrink-0 whitespace-nowrap text-right text-foreground font-semibold text-base tabular-nums">
                            {isPrivateUnlocked ? (
                              `$ ${formatUsdTotal(combinedUsd)}`
                            ) : (
                              <span className="inline-flex items-center gap-1 text-muted-foreground">
                                <Lock className="w-[1em] h-[1em]" strokeWidth={2.5} />
                                <span>••••</span>
                              </span>
                            )}
                          </span>
                        </div>
                      </div>

                      {/* Divider */}
                      <div className="h-px bg-border" />

                      {/* Public balance button */}
                      <button
                        onClick={() => onSelectPublicToken(pair.symbol)}
                        className={`flex flex-col gap-0.5 px-3 py-2 rounded-lg transition-all text-left ${
                          isPublicSelected
                            ? "bg-foreground/10 ring-1 ring-border"
                            : "bg-muted/30 hover:bg-muted"
                        }`}
                        aria-label={`Portal In ${pair.name}: ${pair.publicBalance}`}
                      >
                        <span className="text-[10px] text-muted-foreground font-medium">Public</span>
                        <div className={MOBILE_BALANCE_LINE}>
                          <span className="text-foreground font-semibold text-base tabular-nums text-right truncate">
                            {formatSmartBalance(pair.publicBalance)}
                          </span>
                          <span className="text-foreground/70 dark:text-cyan-300/70 text-xs truncate">{pair.symbol}</span>
                        </div>
                        <div className={MOBILE_BALANCE_LINE}>
                          <span className="text-xs text-muted-foreground tabular-nums text-right truncate">
                            $ {formatUsdAmount(publicUsd)}
                          </span>
                          <span aria-hidden="true" />
                        </div>
                      </button>

                      {/* Private balance button */}
                      <button
                        onClick={() => onSelectPrivateToken(pair.privateSymbol)}
                        className={`flex flex-col gap-0.5 px-3 py-2 rounded-lg transition-all text-left ${
                          isPrivateSelected
                            ? "bg-foreground/10 ring-1 ring-purple-400/40"
                            : "bg-muted/30 hover:bg-muted"
                        }`}
                        aria-label={`Portal Out ${pair.privateSymbol}: ${pair.privateBalance}`}
                      >
                        <span className="text-[10px] text-muted-foreground font-medium">Private</span>
                        <div className={MOBILE_BALANCE_LINE}>
                          <span className="text-foreground font-semibold text-base tabular-nums text-right truncate">
                            {isPrivateUnlocked ? (
                              formatSmartBalance(pair.privateBalance)
                            ) : (
                              <span className="inline-flex items-center justify-end gap-1 w-full">
                                <Lock className="w-[1em] h-[1em] shrink-0" strokeWidth={2.5} />
                                <span>••••</span>
                              </span>
                            )}
                          </span>
                          <span className="text-purple-400 text-xs truncate">{pair.privateSymbol}</span>
                        </div>
                        <div className={MOBILE_BALANCE_LINE}>
                          <span className="text-xs text-muted-foreground tabular-nums text-right truncate">
                            {isPrivateUnlocked ? (
                              `$ ${formatUsdAmount(privateUsd)}`
                            ) : (
                              <span className="inline-flex items-center justify-end gap-1 w-full">
                                <Lock className="w-[1em] h-[1em] shrink-0" strokeWidth={2.5} />
                                <span>••••</span>
                              </span>
                            )}
                          </span>
                          <span aria-hidden="true" />
                        </div>
                      </button>
                    </motion.div>
                  );
                })()}
              </AnimatePresence>
            </div>
        )}
      </div>

      {/* ── DESKTOP: full table list (sm and above) ── */}
      <div className="hidden sm:block overflow-x-auto -mx-4 px-4">
        <div className="min-w-fit rounded-2xl border border-border bg-card shadow-sm">
          {/* Header row */}
          <div className={`${TOKEN_HEADER_GRID} items-center px-4 pt-4 pb-3 text-muted-foreground`}>
            <span className="min-w-0 px-2 md:px-3 text-xs font-semibold leading-snug self-center">Asset</span>
            {/* Balance headers reuse the cell grid so the label right-aligns with the amounts. */}
            <div className={`${PUBLIC_CELL_GRID} px-2 md:px-3`}>
              <span className="whitespace-nowrap text-right text-xs font-semibold leading-snug">
                Public Balance<br />
                <span className="text-[10px] font-normal text-muted-foreground/80">(Visible on-chain)</span>
              </span>
            </div>
            <div className={`${PRIVATE_CELL_GRID} px-2 md:px-3`}>
              <span className="whitespace-nowrap text-right text-xs font-semibold leading-snug">
                Private Balance<br />
                <span className="text-[10px] font-normal text-muted-foreground/80">(Encrypted)</span>
              </span>
            </div>
            <span className="whitespace-nowrap px-2 md:px-3 text-right text-xs font-semibold leading-snug">
              <span className="inline-flex items-center gap-1">
                Total Value
                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        className="inline-flex items-center justify-center cursor-help text-muted-foreground/80"
                        aria-label="Total value information"
                      >
                        <Info className="w-3.5 h-3.5" strokeWidth={2.25} />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="normal-case font-normal tracking-normal">
                      Combined USD value of your public and private balances.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </span><br />
              <span className="text-[10px] font-normal text-muted-foreground/80">(Combined)</span>
            </span>
          </div>

          {filteredPairs.map((pair, index) => {
            const isPublicSelected = selectedSide === "public" && selectedToken === pair.symbol;
            const isPrivateSelected = selectedSide === "private" && selectedToken === pair.privateSymbol;
            const publicUsd = balanceToUsd(pair.symbol, pair.publicBalance, prices);
            const privateUsd = balanceToUsd(pair.symbol, pair.privateBalance, prices);
            const combinedUsd = publicUsd + privateUsd;
            const showPublicAddressTooltip = !!pair.publicAddress;
            // The Midnight icon is a white glyph on a transparent background — invisible
            // against the light-mode page background without a dark backdrop.
            const isMidnightIcon =
              pair.icon.toLowerCase().includes("midnight") ||
              pair.icon.toLowerCase().includes("night") ||
              pair.symbol.toLowerCase() === "night" ||
              pair.name.toLowerCase().includes("midnight");

            return (
              <motion.div
                key={pair.symbol}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.04 }}
                className={`${TOKEN_ROW_GRID} px-4 py-2 border-t border-border/60`}
              >
                {/* Asset — icon + name + symbol */}
                <div
                  className="min-w-0 flex flex-row items-center justify-start gap-2 px-2 md:px-3 py-2 rounded-lg"
                  aria-label={`${pair.name} (${pair.symbol})`}
                >
                  <div
                    className={
                      isMidnightIcon
                        ? "inline-flex rounded-full bg-slate-950 dark:bg-transparent"
                        : "inline-flex"
                    }
                  >
                    <TokenIcon icon={pair.icon} size="md" />
                  </div>
                  <div className="min-w-0 flex flex-col leading-tight">
                    <span className="text-foreground/90 dark:text-cyan-300/70 text-sm font-medium truncate" title={pair.name}>{pair.name}</span>
                  </div>
                </div>

                {/* Public balance */}
                {(() => {
                  const btn = (
                    <button
                      onClick={() => onSelectPublicToken(pair.symbol)}
                      className={`flex flex-col justify-center min-w-0 px-2 md:px-3 py-2 rounded-lg transition-all text-left ${
                        isPublicSelected ? "bg-foreground/10 ring-1 ring-border" : "hover:bg-muted"
                      }`}
                      aria-label={`Portal In ${pair.name}: ${pair.publicBalance}`}
                    >
                      <div className={`${PUBLIC_CELL_GRID} items-baseline`}>
                        <span className="text-foreground font-semibold text-base tabular-nums text-right whitespace-nowrap">
                          {formatSmartBalance(pair.publicBalance)}
                        </span>
                        <span className="text-muted-foreground dark:text-cyan-300/70 text-xs truncate min-w-0">{pair.symbol}</span>
                      </div>
                      <div className={PUBLIC_CELL_GRID}>
                        <span className="text-xs text-muted-foreground tabular-nums text-right">$ {formatUsdAmount(publicUsd)}</span>
                      </div>
                    </button>
                  );
                  if (!showPublicAddressTooltip) return btn;
                  return (
                    <Tooltip>
                      <TooltipTrigger asChild>{btn}</TooltipTrigger>
                      <TooltipContent className="max-w-none overflow-visible">
                        <AddressTooltipContent address={pair.publicAddress} explorerBaseUrl={explorerBaseUrl} copiedAddress={copiedAddress} onCopy={handleCopyAddress} />
                      </TooltipContent>
                    </Tooltip>
                  );
                })()}

                {/* Private balance */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => onSelectPrivateToken(pair.privateSymbol)}
                      className={`relative flex flex-col justify-center min-w-0 px-2 md:px-3 py-2 rounded-lg transition-all text-left ${
                        isPrivateSelected ? "bg-foreground/10 ring-1 ring-purple-400/40" : "hover:bg-muted"
                      }`}
                      aria-label={`Portal Out ${pair.privateSymbol}: ${pair.privateBalance}`}
                    >
                      <div className={`${PRIVATE_CELL_GRID} items-baseline`}>
                        <span className="text-foreground font-semibold text-base tabular-nums text-right whitespace-nowrap">
                          {isPrivateUnlocked ? formatSmartBalance(pair.privateBalance) : (
                            <span className="inline-flex items-center gap-1">
                              <Lock className="w-[1em] h-[1em] shrink-0" strokeWidth={2.5} />
                              <span>••••</span>
                            </span>
                          )}
                        </span>
                        <span className="text-purple-400 text-xs truncate min-w-0">{pair.privateSymbol}</span>
                      </div>
                      <div className={PRIVATE_CELL_GRID}>
                        <div className="flex items-center justify-end gap-2 min-w-0">
                          <span className="text-xs text-muted-foreground tabular-nums">
                            {isPrivateUnlocked ? `$ ${formatUsdAmount(privateUsd)}` : (
                              <span className="inline-flex items-center gap-1">
                                <Lock className="w-[1em] h-[1em] shrink-0" strokeWidth={2.5} />
                                <span>••••</span>
                              </span>
                            )}
                          </span>
                        </div>
                      </div>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-none overflow-visible">
                    <AddressTooltipContent address={pair.privateAddress} explorerBaseUrl={explorerBaseUrl} copiedAddress={copiedAddress} onCopy={handleCopyAddress} />
                  </TooltipContent>
                </Tooltip>

                {/* Total value */}
                <button
                  type="button"
                  onClick={() => {
                    if (!isPrivateUnlocked) {
                      onShowUnlockModal?.();
                    }
                  }}
                  disabled={isPrivateUnlocked}
                  className={`flex flex-col justify-center sm:min-w-max px-2 md:px-3 py-2 rounded-lg text-right transition-all ${
                    isPrivateUnlocked ? "cursor-default" : "hover:bg-muted cursor-pointer"
                  }`}
                  aria-label={
                    isPrivateUnlocked
                      ? `Total value for ${pair.name}: ${formatUsdTotal(combinedUsd)} USD`
                      : `Unlock to view total value for ${pair.name}`
                  }
                >
                  <span className="text-foreground font-semibold text-base tabular-nums whitespace-nowrap">
                    {isPrivateUnlocked ? (
                      <>$ {formatUsdTotal(combinedUsd)}</>
                    ) : (
                      <span className="inline-flex items-center gap-1">
                        <Lock className="w-[1em] h-[1em] shrink-0" strokeWidth={2.5} />
                        <span>••••</span>
                      </span>
                    )}
                  </span>
                  <span className="text-sm leading-tight">&nbsp;</span>
                </button>
              </motion.div>
            );
          })}

          {filteredPairs.length === 0 && (
            <div className="text-center text-muted-foreground text-sm py-8 border-t border-border/60">No tokens found</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CombinedTokenCards;
